import React, { createContext, useContext, useEffect, useReducer } from "react";
import jwtDecode from "jwt-decode";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { decryptData } from "../utils/decrypt";
import { ACCOUNT_INITIALISE, LOGIN, LOGOUT } from "../store/actions";
import axios from "axios";
import accountReducer from "../store/accountReducer";
import Loader from "../components/Loader/Loader";
import { requestAccessApi } from "../api/authApi";
import Swal from "sweetalert2";
import { socket } from "../socket";

const BASE_URL = process.env.REACT_APP_API_URL;

const initialState = {
  isLoggedIn: false,
  isInitialised: false,
  user: null,
};

async function setFingerprintCookie() {
  const fp = await FingerprintJS.load();
  const result = await fp.get();
  const fingerprint = result.visitorId;

  document.cookie = `device_fingerprint=${fingerprint}; path=/; Secure; SameSite=Strict`;
}

setFingerprintCookie();

function getCookie(name) {
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith(name + "="))
    ?.split("=")[1];
}

const verifyToken = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }

  const decoded = jwtDecode(serviceToken);
  return decoded.exp > Date.now() / 1000;
};

const setSession = (serviceToken) => {
  if (serviceToken) {
    localStorage.setItem("serviceToken", serviceToken);
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    localStorage.removeItem("serviceToken");
    delete axios.defaults.headers.common.Authorization;
  }
};

const JWTContext = createContext({
  ...initialState,
  login: () => Promise.resolve(),
  logout: () => {},
  requestAccess: () => Promise.resolve(),
  updateSession: () => {},
});

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(accountReducer, initialState);
  const [reloadSession, setReloadSession] = React.useState(false);

  const login = async (email, password, store, recaptchaToken) => {
    const registerData = {
      ip_usuario: window.location.hostname,
      navegador_usuario: navigator.userAgent,
    };
    const fingerprint = getCookie("device_fingerprint");
    const response = await axios.post(`${BASE_URL}/api/account/login`, {
      email,
      password,
      store,
      registerData,
      fingerprint,
      recaptchaToken,
    });
    const { serviceToken, user, tempToken } = response.data;
    if (response.data.error === "006") {
      const resp = await Swal.fire({
        title: "Dispositivo no autorizado!",
        text: "Desea solicitar acceso?",
        icon: "warning",
        confirmButtonText: "Solicitar",
        showCancelButton: true,
      });

      if (resp.isConfirmed && tempToken) {
        const resp = await requestAccess(tempToken);
        if (resp.success) {
          Swal.fire(
            "Solicitud enviada",
            "Tu acceso está pendiente de aprobación.",
            "success",
          );
        }
      }
      return; // (NO entrar al sistema)
    }
    setSession(serviceToken);
    dispatch({
      type: LOGIN,
      payload: {
        user,
      },
    });
  };

  const logout = async () => {
    try {
      await axios.post(`${BASE_URL}/api/account/logout`);
    } finally {
      socket.disconnect();
      setSession(null);
      localStorage.removeItem("globalValues");
      dispatch({ type: LOGOUT });
    }
  };

  // Actualizar sesión con nuevo token
  const updateSession = (serviceToken, user) => {
    if (serviceToken && user) {
      setSession(serviceToken);
      dispatch({
        type: LOGIN,
        payload: {
          user,
        },
      });
    }
  };

  const setAuthState = (newState) => {
    dispatch({
      type: ACCOUNT_INITIALISE,
      payload: newState,
    });
  };

  async function getIPAddress() {
    try {
      const response = await fetch("https://api64.ipify.org?format=json");
      const data = await response.json();
      return data.ip; // Retorna la IP pública
    } catch (error) {
      console.error("Error obteniendo la IP:", error);
      return null;
    }
  }

  const requestAccess = async (tempToken) => {
    try {
      const fingerprint = getCookie("device_fingerprint");
      const values = {
        fingerprint_hash: fingerprint,
        ip_address: await getIPAddress(),
        user_agent: navigator.userAgent,
      };
      await requestAccessApi(values, tempToken);
    } catch (error) {
      alert(error);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = window.localStorage.getItem("serviceToken");
        if (serviceToken && verifyToken(serviceToken)) {
          setSession(serviceToken);
          const response1 = await axios.get(`${BASE_URL}/log/account/me`);
          const response = decryptData(response1);
          // const adminConfig = await axios.get(`${BASE_URL}/params/get-params/`);
          const { user } = response;
          // const admConfig = decryptData(adminConfig);
          // user.adminConfig = admConfig.adminConfig;
          dispatch({
            type: ACCOUNT_INITIALISE,
            payload: {
              isLoggedIn: true,
              user,
            },
          });
        } else {
          dispatch({
            type: ACCOUNT_INITIALISE,
            payload: {
              isLoggedIn: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: ACCOUNT_INITIALISE,
          payload: {
            isLoggedIn: false,
            user: null,
          },
        });
      }
    };

    init();
  }, [reloadSession]);

  useEffect(() => {
    if (!state.isLoggedIn) return;

    const token = localStorage.getItem("serviceToken");

    if (!token) return;

    socket.auth = { token };

    if (!socket.connected) {
      socket.connect();
    }

    const handleError = (err) => {
      console.error("Socket:", err.message);
    };

    socket.on("connect_error", handleError);

    return () => {
      socket.off("connect_error", handleError);

      // Desconectar al cerrar sesión o desmontar el Provider
      if (socket.connected) {
        socket.disconnect();
      }
    };
  }, [state.isLoggedIn]);

  if (!state.isInitialised) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        logout,
        requestAccess,
        setAuthState,
        setReloadSession,
        updateSession,
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

// Crear y exportar el hook useJwt
export const useJwt = () => {
  return useContext(JWTContext);
};

export default JWTContext;
